import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import MaskedInput from 'react-text-mask';
import { Tooltip } from '@components/common';
import { RECURRENCE_FREQUENCIES, DAYS_OF_WEEK } from '@constants';

import './JobDateTime.scss';

const minStartDate = new Date();
minStartDate.setHours(0, 0, 0, 0);
minStartDate.setDate(minStartDate.getDate() + 4);

const { navigator : { userAgent, vendor, maxTouchPoints } } = window;
const isAppleDevice = vendor.toLowerCase().includes('apple');
const isSafari = isAppleDevice && (/safari/gi).test(userAgent);
const isIPhone = isAppleDevice && (/iphone/gi).test(userAgent);
const isIPad = isAppleDevice && !isIPhone && maxTouchPoints > 0;

const isDesktopSafari = isSafari && !isIPhone && !isIPad;
const isIOSDevice = isIPhone || isIPad;

const timeRegex = isDesktopSafari ? /(01|02|03|04|05|06|07|08|09|10|11|12):([0-59]{2}) ((AM)|(PM))/gi : /([\d]{2}):([\d]{2})/gi;

class JobDateTime extends PureComponent {
	constructor(props) {
		super(props);

		this.initialValues = {
			jobStartDate      : '',
			recurrencyEndDate : '',
			jobStartsAt       : '',
			jobEndsAt         : '',
			jobRepeatsOn      : 1,
			additionalDetails : '',
		};

		// minEndDate is initialized to be one day after minStartDate
		this.state = {
			activeTab         : 'single',
			weeklyOption      : '',
			recurrenceOptions : { ...RECURRENCE_FREQUENCIES },
			minEndDate        : new Date(minStartDate.getTime() + (1000 * 60 * 60 * 24)),
		};

		this.dateMask = [/[0-1]/, /[0-9]/, '/', /[0-3]/, /[0-9]/, '/', /2/, /0/, /[2-9]/, /[0-9]/];
		this.timeMask = [/[0-1]/, /[0-9]/, ':', /[0-5]/, /[0-9]/, ' ', /(A|P)/i, /M/i];
	}

	// Because minEndDate can change, the validation schema needs to by dynamic to accommodate
	getValidationSchema = () => {
		const { activeTab, minEndDate } = this.state;
		if (activeTab === 'single') {
			return Yup.object().shape({
				jobStartDate : Yup.date().required('Start date is required.').min(minStartDate, 'Job date must be at least 4 days from now.')
					.typeError('Please enter a valid date (mm/dd/yyyy).'),
				jobStartsAt       : Yup.string().required('Start time is required.').matches(timeRegex, 'Please enter a valid start time'),
				jobEndsAt         : Yup.string().required('End time is required.').matches(timeRegex, 'Please enter a valid end time'),
				additionalDetails : Yup.string().required('Please provide some additional details about this job request.'),
			});
		}

		return Yup.object().shape({
			jobStartDate : Yup.date().required('Start date is required.').min(minStartDate, 'Start date must be at least 4 days from now.')
				.typeError('Please enter a valid date (mm/dd/yyyy).'),
			recurrencyEndDate : Yup.date().required('End date is required.').min(minEndDate, 'End date must be at least one day after start date.')
				.typeError('Please enter a valid date (mm/dd/yyyy).'),
			jobStartsAt       : Yup.string().required('Start time is required.').matches(timeRegex, 'Please enter a valid start time'),
			jobEndsAt         : Yup.string().required('End time is required.').matches(timeRegex, 'Please enter a valid end time'),
			jobRepeatsOn      : Yup.number().required('Please select a recurrency type.'),
			additionalDetails : Yup.string().required('Please provide some additional details about this job request.'),
		});
	}

	setActiveTab = (activeTab) => { this.setState({ activeTab }); }

	setWeeklyDay = ({ target : { value } }) => {
		const { activeTab } = this.state;
		const dayOfWeek = new Date(`${value} 00:00:00Z`).getUTCDay() + 1;

		if (dayOfWeek) {
			const recurrenceOptions = { ...RECURRENCE_FREQUENCIES };
			const {
				label     : selectedDayOfWeek,
				shortName : weeklyOption,
			} = DAYS_OF_WEEK.find((day) => day.value === dayOfWeek);

			recurrenceOptions.WEEKLY.label = `Weekly (Every ${selectedDayOfWeek})`;
			if (activeTab === 'recurring') { this.setState({ recurrenceOptions, weeklyOption }); }
		}
	}

	onSubmit = (values) => {
		const {
			toNextStep, setJobDateTime, slideToEstimate, accountCreated,
		} = this.props;
		const { activeTab, weeklyOption } = this.state;
		const newForm = {};

		let StartDateTime = new Date(`${values.jobStartDate} ${values.jobStartsAt}`);
		let EndDateTime = new Date(`${values.jobStartDate} ${values.jobEndsAt}`);
		// iOS required specific date formatting to parse into a date object
		if (isIOSDevice) {
			StartDateTime = new Date(`${values.jobStartDate.replace(/-/gi, '/')} ${values.jobStartsAt}`);
			EndDateTime = new Date(`${values.jobStartDate.replace(/-/gi, '/')} ${values.jobEndsAt}`);
		}

		/*
		if job end date is less than job start date
		this is an overnight job.
		We need to add one day to the endDate
		*/
		if (EndDateTime < StartDateTime) {
			EndDateTime.setDate(EndDateTime.getDate() + 1);
		}

		newForm.StartDateTime = StartDateTime;
		newForm.EndDateTime = EndDateTime;

		newForm.JobDetails = values.additionalDetails;

		// save recurring data if required
		if (activeTab === 'recurring') {
			// parsing a date string without a time value will result in JavaScript offsetting
			// the date to local time zone.
			// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/parse
			let EndsOn = new Date(`${values.recurrencyEndDate} ${values.jobEndsAt}`);
			// Just like StartDateTime and EndDateTime, we must format differently if on iOS
			if (isIOSDevice) {
				EndsOn = new Date(`${values.recurrencyEndDate.replace(/-/gi, '/')} ${values.jobEndsAt}`);
				// iOS browsers do not fire input events, so this check must be in place to compensate
				if (EndsOn < new Date(StartDateTime.getTime() + (1000 * 60 * 60 * 24))) {
					// eslint-disable-next-line no-alert
					alert('End date must be at least one day after start date.');
					return;
				}
			}

			const RepeatType = values.jobRepeatsOn;
			let RepeatsOn = '';

			// set RepeatsOn depending if is weekdays or weekends
			switch (RepeatType) {
				case '2':
					RepeatsOn = 'mon,tue,wed,thu,fri';
					break;
				case '3':
					RepeatsOn = 'sat,sun';
					break;
				case '4':
					RepeatsOn = weeklyOption;
					break;
				default:
					break;
			}

			newForm.Recurrence = { EndsOn, RepeatType, RepeatsOn };
		}

		setJobDateTime(newForm);
		if (accountCreated) slideToEstimate();
		else toNextStep();
	}

	formatMinimumDate = (date) => {
		const year = date.getFullYear();
		const month = (date.getMonth() + 1 < 10) ? `0${date.getMonth() + 1}` : date.getMonth() + 1;
		const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate();

		return `${year}-${month}-${day}`;
	}

	updateMinEndDate = (event, handleBlur) => {
		const { target : { value } } = event;
		if (!Number.isNaN(new Date(`${value} 00:00:00`).getTime())) {
			const minEndDate = new Date(Math.max(minStartDate.getTime(), new Date(`${value} 00:00:00`).getTime()));
			minEndDate.setDate(minEndDate.getDate() + 1);
			this.setState({ minEndDate });
		}
		handleBlur(event);
	}

	render() {
		const { toPreviousStep, JobEventType } = this.props;
		const { activeTab, minEndDate, recurrenceOptions } = this.state;
		let startDateContainerClass = 'col-sm-5 col-sm-offset-1';
		if (activeTab === 'single') startDateContainerClass = 'col-sm-6 col-sm-offset-3';
		if (isIOSDevice) startDateContainerClass += ' text-left';
		return (
			<div className="form-panel panel panel-default text-center" id="job-datetime-form">
				<div className="panel-heading">
					<h4 className="panel-title text-primary">{ JobEventType }</h4>
				</div>
				<div className="panel-body container-fluid">
					<Formik
						initialValues={this.initialValues}
						validationSchema={this.getValidationSchema()}
						validateOnChange={false}
						validateOnBlur={false}
						onSubmit={this.onSubmit}
					>
						{({ errors, handleBlur }) => (
							<Form>
								<div className="row panel-row" id="recurrency-toggle">
									<ul className="nav nav-pills">
										<li
											role="presentation"
											className={activeTab === 'single' ? 'active' : ''}
											onClick={() => this.setActiveTab('single')}
										>
											One Time
										</li>
										<li
											role="presentation"
											className={activeTab === 'recurring' ? 'active' : ''}
											onClick={() => this.setActiveTab('recurring')}
										>
											Recurring
										</li>
									</ul>
								</div>
								<div className="row panel-row" id="job-dates">
									<div className={startDateContainerClass}>
										<div className="label-tooltip">
											<label htmlFor="jobStartDate" className="control-label">{activeTab === 'single' ? 'Date' : 'Start Date'}</label>
											<Tooltip content="This is the date the job begins.">
												<i
													className="icon-tooltip rkf rkf-question-solid"
												/>
											</Tooltip>
										</div>
										<Choose>
											<When condition={isDesktopSafari}>
												<Field
													name="jobStartDate"
													render={({ field }) => (
														<MaskedInput
															// eslint-disable-next-line react/jsx-props-no-spreading
															{...field}
															id="jobStartDate"
															type="text"
															onBlur={(event) => {
																this.setWeeklyDay(event);
																this.updateMinEndDate(event, handleBlur);
															}}
															mask={this.dateMask}
															className="form-control"
															placeholder="mm/dd/yyyy"
														/>
													)}
												/>
											</When>
											<Otherwise>
												<Field
													name="jobStartDate"
													id="jobStartDate"
													className="form-control"
													type="date"
													onBlur={(event) => {
														this.setWeeklyDay(event);
														this.updateMinEndDate(event, handleBlur);
													}}
													min={this.formatMinimumDate(minStartDate)}
												/>
											</Otherwise>
										</Choose>
										<small className="text-danger">{errors.jobStartDate}</small>
									</div>
									<If condition={activeTab === 'recurring'}>
										<div className={`end-date-container col-sm-5${isIOSDevice ? ' text-left' : ''}`}>
											<div className="label-tooltip">
												<label htmlFor="recurrencyEndDate" className="control-label">End Date</label>
												<Tooltip content="This is the date the job ends.">
													<i
														className="icon-tooltip rkf rkf-question-solid"
													/>
												</Tooltip>
											</div>
											<Choose>
												<When condition={isDesktopSafari}>
													<Field
														name="recurrencyEndDate"
														render={({ field }) => (
															<MaskedInput
																// eslint-disable-next-line react/jsx-props-no-spreading
																{...field}
																id="recurrencyEndDate"
																type="text"
																mask={this.dateMask}
																className="form-control"
																placeholder="mm/dd/yyyy"
															/>
														)}
													/>
												</When>
												<Otherwise>
													<Field
														name="recurrencyEndDate"
														id="recurrencyEndDate"
														className="form-control"
														type="date"
														min={this.formatMinimumDate(minEndDate)}
													/>
												</Otherwise>
											</Choose>
											<small className="text-danger">{errors.recurrencyEndDate}</small>
										</div>
									</If>
								</div>
								<div className="row panel-row" id="job-times">
									<div className={`col-sm-4 col-sm-offset-1 no-pad-right${isIOSDevice ? ' text-left' : ''}`}>
										<label htmlFor="jobStartsAt" className="control-label">Start Time</label>
										<Choose>
											<When condition={isDesktopSafari}>
												<Field
													name="jobStartsAt"
													render={({ field }) => (
														<MaskedInput
															// eslint-disable-next-line react/jsx-props-no-spreading
															{...field}
															id="jobStartsAt"
															type="text"
															mask={this.timeMask}
															className="form-control"
															placeholder="__:__ __"
														/>
													)}
												/>
											</When>
											<Otherwise>
												<Field
													name="jobStartsAt"
													id="jobStartsAt"
													className="form-control"
													type="time"
												/>
											</Otherwise>
										</Choose>
										<small className="text-danger">{errors.jobStartsAt}</small>
									</div>
									<div className="col-sm-2 from-to text-gray">
										to
									</div>
									<div className={`col-sm-4 no-pad-left${isIOSDevice ? ' text-left' : ''}`}>
										<label htmlFor="jobEndsAt" className="control-label">End Time</label>
										<Choose>
											<When condition={isDesktopSafari}>
												<Field
													name="jobEndsAt"
													render={({ field }) => (
														<MaskedInput
															// eslint-disable-next-line react/jsx-props-no-spreading
															{...field}
															id="jobEndsAt"
															type="text"
															mask={this.timeMask}
															className="form-control"
															placeholder="__:__ __"
														/>
													)}
												/>
											</When>
											<Otherwise>
												<Field
													name="jobEndsAt"
													id="jobEndsAt"
													className="form-control"
													type="time"
												/>
											</Otherwise>
										</Choose>
										<small className="text-danger">{errors.jobEndsAt}</small>
									</div>
								</div>
								<If condition={activeTab === 'recurring'}>
									<div className="row panel-row" id="recurrency-type">
										<div className="col-sm-10 col-sm-offset-1">
											<div className="label-tooltip">
												<label htmlFor="jobRepeatsOn" className="control-label">Job Repeats</label>
												<Tooltip content="Is this job going to occur on a regular schedule?">
													<i
														className="icon-tooltip rkf rkf-question-solid"
													/>
												</Tooltip>
											</div>
											<Field
												as="select"
												name="jobRepeatsOn"
												id="jobRepeatsOn"
												className="form-control"
											>
												<For
													each="item"
													of={Object.values(recurrenceOptions)}
												>
													<option key={`marketplace_jobRepeatsOn_${item.value}`} value={item.value}>{item.label}</option>
												</For>
											</Field>
											<small className="text-danger">{errors.jobRepeatsOn}</small>
										</div>
									</div>
								</If>
								<div className="row panel-row" id="additional-info">
									<div className="col-sm-10 col-sm-offset-1">
										<div className="label-tooltip">
											<label htmlFor="additionalDetails" className="control-label">Additional Job Details</label>
											<Tooltip content="Is there anything else we should know about the job?">
												<i
													className="icon-tooltip rkf rkf-question-solid"
												/>
											</Tooltip>
										</div>
										<Field
											as="textarea"
											name="additionalDetails"
											id="additionalDetails"
											className="form-control"
										/>
										<small className="text-danger">{errors.additionalDetails}</small>
									</div>
								</div>
								<div className="row marketplace-action-buttons-container">
									<button
										type="button"
										className="btn btn-link col-xs-4 col-sm-offset-2 col-xs-offset-1"
										onClick={toPreviousStep}
									>
										Back
									</button>
									<button
										type="submit"
										className="btn btn-primary col-xs-6 col-sm-4"
									>
										Next
									</button>
								</div>
							</Form>
						)}
					</Formik>
				</div>
			</div>
		);
	}
}

JobDateTime.propTypes = {
	toNextStep      : PropTypes.func.isRequired,
	toPreviousStep  : PropTypes.func.isRequired,
	setJobDateTime  : PropTypes.func.isRequired,
	slideToEstimate : PropTypes.func.isRequired,
	JobEventType    : PropTypes.string.isRequired,
	accountCreated  : PropTypes.bool.isRequired,
};

export default JobDateTime;
